/**
 * The glyph a surface renders when a value is genuinely absent.
 *
 * WHY A DASH RATHER THAN A BLANK CELL. A blank is ambiguous between three
 * different things a reader cannot tell apart: "there is no value", "this is
 * still loading", and "this failed to render". A dash is a positive assertion
 * that we looked and there is nothing there. It screen-reads as content too,
 * where a blank is silence, indistinguishable from a cell that was skipped.
 *
 * This is not theoretical. The Billing "Next invoice date" tile rendered EMPTY
 * while its three neighbours rendered a dash, purely because that one call site
 * was missing a `?? '—'` fallback — and it was read as a bug. That is the whole
 * argument for a single constant AND for the emptiness decision living inside
 * the shared components (see `InfoItem`) rather than at N call sites, any one of
 * which can forget.
 *
 * WHY AN EM DASH AND NOT A HYPHEN. This is the empty-value GLYPH, a typographic
 * convention, not prose — so working-conventions §15 (no em-dashes in UI copy)
 * does not apply. §15 is about em-dashes joining clauses in sentences.
 *
 * WHY NOT A WORD. A word ("Unclaimed", "None", "N/A") has to be re-decided per
 * surface and drifts; worse, it asserts a specific reason for the emptiness that
 * is usually derivable from context and adds only ink.
 *
 * Read by both `apps/platform` and `apps/app` through `@repo/ui` so the two
 * cannot drift onto different characters (working-conventions §36/§44).
 */
export const EMPTY_VALUE = '—'

/**
 * Whether a value should render as {@link EMPTY_VALUE}.
 *
 * Deliberately NOT a `!value` falsy check: `0` and `false` are VALUES, not
 * emptiness — "0 activations" and "no answer" are different statements, and a
 * falsy check silently turns the first into the second. Whitespace-only strings
 * DO count, since they render identically to a blank.
 *
 * NOTE that `undefined` counts as empty here, which is the honest general
 * answer. A component that gives `undefined` a second meaning — `InfoItem` uses
 * it as its caller's "still loading" signal — must therefore test for that
 * BEFORE calling this, so a skeleton can never collapse into a dash (§19).
 */
export const isEmptyValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '')
